import React from "react";
import SplitText from "./Animations/SplitText";
import cn from "classnames";

/**
 * @description Page Header Component
 * Can be called in different places, like the main page title
 * Can be called in different places, like the second level heading
 * @param background
 * @param backgroundOpacity
 * @param color
 * @param title
 * @param opacity
 * @param position
 * @param size
 * @param spaces
 * @param implemented
 * @param className
 * @param isGeneral
 * @param animateTitle
 * @returns {JSX.Element}
 * @constructor
 * @example
 * 	<PageHeader
 * 		{...pageHeader}
 *      title={pageHeader?.title || title}
 *      implemented={true}
	 *      className={"section-title section-title--large blog-posts-filter__title"}
	 *      isGeneral={true}
	 *      animateTitle={false}
	 *  />
 */
const PageHeader = ({
	background,
	backgroundOpacity,
	color,
	title,
	opacity,
	position,
	size,
	spaces,
	implemented = false,
	className,
	isGeneral = false,
	animateTitle = true,
}) => {
	const defaultClasses = cn(
		"section-title",
		"section-title--large",
		"section-title--black",
		"text-center",
	);
	const { top, bottom, leftright, units } = spaces || {};
	const { desktop: desktopSize, tablet: tabletSize, mobile: mobileSize } = size || {};
	const classes = cn(
		className || defaultClasses,
		(desktopSize || tabletSize || mobileSize) && 'page-header-custom',
		desktopSize && 'page-header-custom--desktop-size',
		tabletSize && 'page-header-custom--tablet-size',
		mobileSize && 'page-header-custom--mobile-size',
	);

	// These values must be present in SSR HTML, before hydration or effects.
	const withOpacity = (value, alpha) => {
		if (!value) return undefined;
		const normalized = alpha == null ? 1 : alpha > 0 && alpha <= 1 ? alpha : alpha / 100;
		return `${value}${Math.round(normalized * 255).toString(16).padStart(2, '0')}`;
	};
	const backgroundColor = withOpacity(background, backgroundOpacity);
	const hasSpacing = value => value != null && value !== "" && value >= 0;
	const showCustomSpaces = [top, bottom, leftright].some(hasSpacing);
	const headerStyle = {
		...(hasSpacing(top) && { paddingTop: `${top}${units || "px"}` }),
		...(hasSpacing(bottom) && { paddingBottom: `${bottom}${units || "px"}` }),
		...(hasSpacing(leftright) && {
			paddingLeft: `${leftright}${units || "px"}`,
			paddingRight: `${leftright}${units || "px"}`,
		}),
		color: withOpacity(color, opacity),
		backgroundColor,
		...(desktopSize && { '--font-size-desktop': `${desktopSize}px` }),
		...(tabletSize && { '--font-size-tablet': `${tabletSize}px` }),
		...(mobileSize && { '--font-size-mobile': `${mobileSize}px` }),
		borderRadius: implemented ? "25px" : "0px",
	};
	const headerTag = isGeneral ? 'h1' : 'h2';
	const headerMarkup = Element => (
		<Element className={classes} style={headerStyle}>
			{title ? animateTitle ? <SplitText>{title}</SplitText> : title : null}
		</Element>
	);

	return (
		<>
			{implemented
				? headerMarkup(headerTag)
				: (
					<section className={!showCustomSpaces ? "page-header" : 'page-header-custom-section'} style={{backgroundColor}}>
						{headerMarkup(headerTag)}
					</section>
				)
			}
		</>
	);
};

export default PageHeader;
